import { useCallback, useEffect, useMemo, useState } from "react";
import { Target, Users2, CheckCircle2, Clock, List, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatCard from "@/components/StatCard";
import CondominioPanelDialog from "@/components/CondominioPanelDialog";
import { listCondominios, createCondominio, updateCondominio, deleteCondominio, listVendedores, listClientes, listProspectos } from "@/lib/api";
import { fmtInt, fmtPct, pct, exportCSV } from "@/lib/format";
import { fetchCNPJ } from "@/lib/brasilapi";
import { useAuth, isVendedora, isAdmin } from "@/lib/auth";
import SemConsultorAlert from "@/components/condominios/SemConsultorAlert";
import CondominiosToolbar from "@/components/condominios/CondominiosToolbar";
import CondominiosTable from "@/components/condominios/CondominiosTable";
import CondominioFormDialog from "@/components/condominios/CondominioFormDialog";
import CondominiosMapa from "@/components/condominios/CondominiosMapa";
import {
  CONSULTOR_FILTER_ALL, CONSULTOR_FILTER_NONE, META_FILTER_ALL, emptyForm, newContato, onlyDigits, isValidCNPJ,
} from "@/components/condominios/constants";

export default function CondominiosTab() {
  const { user } = useAuth();
  const isVend = isVendedora(user);
  const admin = isAdmin(user);
  const myVendedorName = user?.vendedor_name || "";
  const [items, setItems] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("todos");
  const [metaFilter, setMetaFilter] = useState(META_FILTER_ALL);
  // Fev/2026: vendedora começa vendo TODOS os condomínios (atendidos +
  // prospectados) — pode filtrar pelo próprio nome quando quiser, mas o default
  // é enxergar a operação inteira pra identificar oportunidades e prédios
  // quentes de outros consultores.
  const [consultorFilter, setConsultorFilter] = useState(CONSULTOR_FILTER_ALL);
  const [tab, setTab] = useState("atendido"); // atendido | prospecto
  const [view, setView] = useState("lista"); // lista | mapa
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // Painel do condomínio (clientes + prospectos vinculados)
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelCondo, setPanelCondo] = useState(null);
  const [allClientes, setAllClientes] = useState([]);
  const [allProspectos, setAllProspectos] = useState([]);
  // BrasilAPI CNPJ lookup — auto-preenche endereço a partir do CNPJ válido.
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjLoadedFor, setCnpjLoadedFor] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, vlist, cli, prosp] = await Promise.all([
        listCondominios(),
        listVendedores().catch(() => []),
        listClientes().catch(() => []),
        listProspectos().catch(() => []),
      ]);
      setItems(data);
      setVendedores((vlist || []).map((v) => v.vendedor_name).filter(Boolean));
      setAllClientes(cli || []);
      setAllProspectos(prosp || []);
    } catch (err) {
      console.error("Erro ao carregar condomínios:", err);
      toast.error("Erro ao carregar condomínios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Métricas de clientes por condomínio: total realizado (histórico) + mês corrente.
  // Base: `qtd_clientes_atuais` (manual/campo) + count de Clientes vinculados via `condominio`.
  const clientesPorCondo = useMemo(() => {
    const now = new Date();
    const mesAtualPrefix = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
    const byNome = {};
    for (const c of allClientes) {
      const key = (c.condominio || "").trim().toLowerCase();
      if (!key) continue;
      const bucket = byNome[key] || (byNome[key] = { total: 0, mes: 0 });
      bucket.total += 1;
      if ((c.created_at || "").startsWith(mesAtualPrefix)) bucket.mes += 1;
    }
    return byNome;
  }, [allClientes]);

  const metricsForCondo = useCallback((condo) => {
    const b = clientesPorCondo[(condo.condominio || "").trim().toLowerCase()] || { total: 0, mes: 0 };
    // Total realizado = maior entre o count real e o campo manual (compat com legado).
    const realizadoTotal = Math.max(b.total, Number(condo.qtd_clientes_atuais || 0));
    return {
      realizadoTotal,
      realizadoMes: b.mes,
      metaTotal: Number(condo.meta_clientes_total || 0),
      metaMensal: Number(condo.meta_clientes_mensal || 0),
    };
  }, [clientesPorCondo]);

  const openPanel = (condo) => {
    setPanelCondo(condo);
    setPanelOpen(true);
  };

  const itemsByStatus = useMemo(() => {
    const atendido = items.filter((i) => (i.infra_status || "atendido") === "atendido");
    const prospecto = items.filter((i) => i.infra_status === "prospecto");
    return { atendido, prospecto };
  }, [items]);

  const current = tab === "atendido" ? itemsByStatus.atendido : itemsByStatus.prospecto;

  // Alerta: condomínios atendidos sem consultor atribuído (visível só para admin)
  const semConsultor = useMemo(
    () => itemsByStatus.atendido.filter((c) => !(c.consultor || "").trim()),
    [itemsByStatus]
  );
  const focarSemConsultor = () => {
    setTab("atendido");
    setConsultorFilter(CONSULTOR_FILTER_NONE);
    setSearch("");
    setTipoFilter("todos");
    setMetaFilter(META_FILTER_ALL);
  };

  const filtered = useMemo(() => {
    return current.filter((i) => {
      const s = search.toLowerCase();
      const matchSearch =
        !search ||
        i.condominio.toLowerCase().includes(s) ||
        (i.responsavel || "").toLowerCase().includes(s) ||
        (i.consultor || "").toLowerCase().includes(s);
      const matchTipo = tipoFilter === "todos" || i.tipo === tipoFilter;
      const matchConsultor =
        consultorFilter === CONSULTOR_FILTER_ALL ? true
          : consultorFilter === CONSULTOR_FILTER_NONE ? !(i.consultor || "").trim()
          : (i.consultor || "") === consultorFilter;
      // Filtro por % de meta — só aplicável na aba "atendido" (prospecto não tem meta_vendas).
      let matchMeta = true;
      if (tab === "atendido" && metaFilter !== META_FILTER_ALL) {
        const [dir, thrStr] = metaFilter.split("_");
        const thr = Number(thrStr);
        const p = pct(i.vendas_realizadas, i.meta_vendas);
        // Condomínios sem meta_vendas cadastrada (>0) ficam fora do filtro
        // para não poluir os resultados com "divisão por zero".
        if (!Number(i.meta_vendas) || i.meta_vendas <= 0) matchMeta = false;
        else if (dir === "abaixo") matchMeta = p < thr;
        else if (dir === "acima") matchMeta = p >= thr;
      }
      return matchSearch && matchTipo && matchConsultor && matchMeta;
    });
  }, [current, search, tipoFilter, consultorFilter, tab, metaFilter]);

  // KPIs por aba
  const totals = useMemo(() => {
    const list = current;
    const totalApt = list.reduce((s, i) => s + Number(i.qtd_apartamentos || 0), 0);
    const totalClientes = list.reduce((s, i) => s + Number(i.qtd_clientes_atuais || 0), 0);
    const totalMeta = list.reduce((s, i) => s + Number(i.meta_vendas || 0), 0);
    const totalVendas = list.reduce((s, i) => s + Number(i.vendas_realizadas || 0), 0);
    return {
      totalCondos: list.length,
      totalApt,
      totalClientes,
      atingimento: pct(totalVendas, totalMeta),
      // exclusivo prospecto: quantos têm data de aprovação prevista
      comPrevisao: list.filter((i) => (i.data_aprovacao_prevista || "").length > 0).length,
    };
  }, [current]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, infra_status: tab }); // já cria no tab ativo
    setCnpjLoadedFor("");
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm, ...item, infra_status: item.infra_status || "atendido" });
    setCnpjLoadedFor(onlyDigits(item.cnpj || ""));
    setOpen(true);
  };

  // Auto-fetch BrasilAPI quando usuário termina de digitar um CNPJ válido.
  useEffect(() => {
    if (!open) return;
    const digits = onlyDigits(form.cnpj);
    if (digits.length !== 14 || !isValidCNPJ(digits)) return;
    if (cnpjLoadedFor === digits) return;
    let cancelled = false;
    (async () => {
      setCnpjLoading(true);
      try {
        const info = await fetchCNPJ(digits);
        if (cancelled) return;
        setCnpjLoadedFor(digits);
        setForm((prev) => ({
          ...prev,
          // Só preenche se estiverem vazios — não sobrescreve edição manual.
          condominio: prev.condominio || info.nome_fantasia || info.razao_social || prev.condominio,
          endereco: prev.endereco || info.endereco_completo || prev.endereco,
        }));
        toast.success("Dados do CNPJ carregados da Receita");
      } catch (err) {
        if (!cancelled) toast.error(err?.message || "CNPJ não encontrado");
      } finally {
        if (!cancelled) setCnpjLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [form.cnpj, open, cnpjLoadedFor]);

  const handleSave = async () => {
    if (!form.condominio.trim()) {
      toast.error("Informe o nome do condomínio");
      return;
    }
    const payload = {
      ...form,
      qtd_apartamentos: Number(form.qtd_apartamentos) || 0,
      qtd_clientes_atuais: Number(form.qtd_clientes_atuais) || 0,
      meta_vendas: Number(form.meta_vendas) || 0,
      vendas_realizadas: Number(form.vendas_realizadas) || 0,
      meta_clientes_total: Number(form.meta_clientes_total) || 0,
      meta_clientes_mensal: Number(form.meta_clientes_mensal) || 0,
    };
    try {
      if (editing) {
        await updateCondominio(editing.id, payload);
        toast.success("Condomínio atualizado");
      } else {
        await createCondominio(payload);
        toast.success("Condomínio cadastrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      console.error("Erro ao salvar condomínio:", err);
      toast.error("Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este condomínio?")) return;
    try {
      await deleteCondominio(id);
      toast.success("Condomínio excluído");
      load();
    } catch (err) {
      console.error("Erro ao excluir condomínio:", err);
      toast.error("Erro ao excluir");
    }
  };

  const handleConverterEmAtendido = async (item) => {
    if (!window.confirm(`Marcar "${item.condominio}" como atendido? A infraestrutura foi aprovada.`)) return;
    try {
      await updateCondominio(item.id, { ...item, infra_status: "atendido" });
      toast.success("Condomínio movido para atendidos");
      load();
    } catch (err) {
      console.error("Erro ao converter condomínio:", err);
      toast.error("Erro ao converter");
    }
  };

  const handleExport = () => {
    const rows = filtered.map((i) => ({
      ...i,
      porcentagem_atingida: pct(i.vendas_realizadas, i.meta_vendas).toFixed(1) + "%",
      porcentagem_penetracao: pct(i.qtd_clientes_atuais, i.qtd_apartamentos).toFixed(1) + "%",
    }));
    exportCSV(`condominios-${tab}.csv`, rows, [
      { key: "condominio", label: "Condomínio" },
      { key: "cnpj", label: "CNPJ" },
      { key: "endereco", label: "Endereço" },
      { key: "complemento", label: "Complemento" },
      { key: "tipo", label: "Tipo" },
      { key: "infra_status", label: "Situação" },
      { key: "qtd_apartamentos", label: "Qtd Apartamentos" },
      { key: "qtd_clientes_atuais", label: "Clientes Atuais" },
      { key: "porcentagem_penetracao", label: "% Penetração" },
      { key: "responsavel", label: "Responsável" },
      { key: "consultor", label: "Consultor" },
      { key: "status_contato", label: "Status Contato" },
      { key: "semana_planejada", label: "Semana Planejada" },
      { key: "data_aprovacao_prevista", label: "Aprovação prevista" },
      { key: "meta_vendas", label: "Meta de Vendas" },
      { key: "vendas_realizadas", label: "Vendas Realizadas" },
      { key: "porcentagem_atingida", label: "% Atingido" },
      { key: "observacoes", label: "Observações" },
    ]);
    toast.success("CSV exportado");
  };

  const setF = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Contatos — helpers para manipular a lista dentro do form.
  const addContato = () => setForm((p) => ({ ...p, contatos: [...(p.contatos || []), newContato()] }));
  const updateContato = (id, k, v) => setForm((p) => ({
    ...p,
    contatos: (p.contatos || []).map((c) => (c.id === id ? { ...c, [k]: v } : c)),
  }));
  const removeContato = (id) => setForm((p) => ({
    ...p,
    contatos: (p.contatos || []).filter((c) => c.id !== id),
  }));

  const isProsp = tab === "prospecto";

  return (
    <div className="space-y-6">
      {admin && semConsultor.length > 0 && (
        <SemConsultorAlert semConsultor={semConsultor} openPanel={openPanel} focarSemConsultor={focarSemConsultor} />
      )}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-xl" data-testid="condominio-tabs">
          <TabsTrigger value="atendido" data-testid="tab-atendido">
            <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
            Atendidos
            <Badge variant="outline" className="ml-2 tabular-nums">{itemsByStatus.atendido.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="prospecto" data-testid="tab-prospecto">
            <Clock className="w-4 h-4 mr-2 text-amber-500" />
            Prospectos
            <Badge variant="outline" className="ml-2 tabular-nums">{itemsByStatus.prospecto.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {["atendido", "prospecto"].map((t) => (
          <TabsContent key={t} value={t} className="space-y-6 mt-4">
            {/* KPIs por aba */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                testId={`kpi-total-condominios-${t}`}
                label={t === "atendido" ? "Condomínios atendidos" : "Condomínios prospectados"}
                value={fmtInt(totals.totalCondos)}
                icon={t === "atendido" ? CheckCircle2 : Clock}
                tone={t === "atendido" ? "success" : "warning"}
              />
              <StatCard
                testId={`kpi-total-apartamentos-${t}`}
                label="Apartamentos/Salas"
                value={fmtInt(totals.totalApt)}
                icon={Users2}
              />
              {t === "atendido" ? (
                <>
                  <StatCard testId="kpi-total-clientes" label="Clientes atuais"
                    value={fmtInt(totals.totalClientes)} icon={Users2} tone="warning" />
                  <StatCard
                    testId="kpi-meta-atingida-porcentagem"
                    label="Meta atingida (global)"
                    value={fmtPct(totals.atingimento)}
                    hint="Vendas / Meta total"
                    icon={Target}
                    tone={totals.atingimento >= 100 ? "success" : totals.atingimento >= 50 ? "warning" : "danger"}
                  />
                </>
              ) : (
                <>
                  <StatCard testId="kpi-prospecto-potencial" label="Potencial de clientes"
                    value={fmtInt(totals.totalApt)}
                    hint="Todas as unidades a atender"
                    icon={Target} tone="warning" />
                  <StatCard testId="kpi-prospecto-com-previsao" label="Com data prevista"
                    value={fmtInt(totals.comPrevisao)}
                    hint={`de ${fmtInt(totals.totalCondos)} · previsão de aprovação`}
                    icon={Clock} />
                </>
              )}
            </div>

            <CondominiosToolbar
              t={t} isProsp={isProsp} search={search} setSearch={setSearch}
              tipoFilter={tipoFilter} setTipoFilter={setTipoFilter} metaFilter={metaFilter} setMetaFilter={setMetaFilter}
              consultorFilter={consultorFilter} setConsultorFilter={setConsultorFilter}
              isVend={isVend} myVendedorName={myVendedorName} vendedores={vendedores}
              handleExport={handleExport} openCreate={openCreate}
            />

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground" data-testid="condominios-count">
                {fmtInt(filtered.length)} condomínio(s) nesta visão
              </p>
              <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="view-toggle-condominios">
                {[{ v: "lista", label: "Lista", Icon: List }, { v: "mapa", label: "Mapa", Icon: MapIcon }].map(({ v, label, Icon }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    data-testid={`btn-view-${v}`}
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {view === "mapa" ? (
              <CondominiosMapa condominios={filtered} onOpenPanel={openPanel} />
            ) : (
              <CondominiosTable
                loading={loading} filtered={filtered} isProsp={isProsp} metricsForCondo={metricsForCondo}
                openPanel={openPanel} handleConverterEmAtendido={handleConverterEmAtendido}
                openEdit={openEdit} handleDelete={handleDelete}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>

      <CondominioFormDialog
        open={open} setOpen={setOpen} editing={editing} form={form} setF={setF}
        cnpjLoading={cnpjLoading} cnpjLoadedFor={cnpjLoadedFor} vendedores={vendedores} handleSave={handleSave}
        addContato={addContato} updateContato={updateContato} removeContato={removeContato}
      />

      <CondominioPanelDialog
        open={panelOpen}
        onOpenChange={setPanelOpen}
        condominio={panelCondo}
        clientes={allClientes}
        prospectos={allProspectos}
      />
    </div>
  );
}
